import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import Typography from "@mui/material/Typography";
import { SNETFileUpload, AlertBox, alertTypes } from "shared/dist/components";
import isEmpty from "lodash/isEmpty";
import { aiServiceDetailsActions, loaderActions } from "../../../Services/Redux/actionCreators";
import { LoaderContent } from "../../../Utils/Loader";
import JSZip from "jszip";
import { isIndexFileInsideFolder } from "../../../Utils/ValidateCompressedFiles";
import { messagesAlerts, handleStatusTypes } from "./DemoFilesStatus";
import { assetTypes, fileTypes } from "../../../Utils/FileUpload";
import classes from "./Demo.module.css";

let isWaitingChangeDemoStatus = false;
let isDemoFilesUploading = false;
const waitingChangeDemoStatusTimeout = 1000;

const UploadDemoFiles = ({
    demoFiles,
    error,
    selectedFile,
    uploadDemoFilesDetails,
    resestNotification,
}) => {
    const { orgUuid, serviceUuid } = useParams();
    const demoStatusRef = useRef(demoFiles.status);
    const [alert, setAlert] = useState();
    const dispatch = useDispatch();

    useEffect(() => {
        if (error) {
            setAlert({ type: alertTypes.WARNING, message: messagesAlerts.hasNotFiles });
        }
    }, [error]);

    const getServiceStatusWithTimeout = async (timeout) => {
        await new Promise((resolve) => setTimeout(resolve, timeout));
        demoStatusRef.current = await getDemoStatus();
    };

    const waitingChangeDemoStatus = async (timeout) => {
        if (isWaitingChangeDemoStatus) {
            return;
        }
        while (demoStatusRef.current !== handleStatusTypes.PENDING) {
            await getServiceStatusWithTimeout(timeout);
            isWaitingChangeDemoStatus = true;
        }
        isWaitingChangeDemoStatus = false;
    };

    const getDemoStatus = async () => {
        try {
            const newDemoStatus = await dispatch(
                aiServiceDetailsActions.getHandlingFileStatus(
                    orgUuid,
                    serviceUuid,
                    fileTypes.demoFiles
                )
            );
            return newDemoStatus;
        } catch (error) {
            setAlert({
                type: alertTypes.ERROR,
                message: "Please, try again",
            });
        }
    };

    const modalTimeout = async () => {
        try {
            dispatch(loaderActions.startAppLoader(LoaderContent.UPLOAD_FILE));
            await waitingChangeDemoStatus(waitingChangeDemoStatusTimeout);
        } catch (err) {
            console.error("upload UI error: ", err);
        } finally {
            dispatch(loaderActions.stopAppLoader());
        }
    };

    const handleFileDrop = async (acceptedFiles, rejectedFiles) => {
        if (isDemoFilesUploading) {
            return;
        }
        isDemoFilesUploading = true;
        if (!isEmpty(rejectedFiles)) {
            setAlert({
                type: alertTypes.ERROR,
                message: messagesAlerts.rejectedFile,
            });
            return;
        }
        if (isEmpty(acceptedFiles)) {
            return;
        }
        try {
            const fileBlob = acceptedFiles[0];
            await validateIndexFile(fileBlob);
            const { name, size, type } = fileBlob;
            uploadDemoFilesDetails({ name, size, type });
            dispatch(loaderActions.startAppLoader(LoaderContent.UPLOAD_FILE));
            const { url } = await dispatch(
                aiServiceDetailsActions.uploadFile(
                    assetTypes.SERVICE_PAGE_COMPONENTS,
                    fileBlob,
                    orgUuid,
                    serviceUuid
                )
            );
            if (!url) {
                throw new Error("Something went wrong...");
            }
            dispatch(aiServiceDetailsActions.setServiceDemoFilesUrl(url));
            dispatch(aiServiceDetailsActions.setServiceTouchedFlag(true));
            await modalTimeout(); // waiting update on back from s3
            resestNotification();
        } catch (e) {
            console.error("error in demo files uploading", e);
            setAlert({ type: alertTypes.ERROR, message: e.message });
        } finally {
            isDemoFilesUploading = false;
            dispatch(loaderActions.stopAppLoader());
        }
    };

    const validateIndexFile = async (uploadedFile) => {
        const fileToBePresent = "index.js";

        const zip = new JSZip();
        await zip.loadAsync(uploadedFile).then((entry) => {
            const indexFileInsideSomeFolder = isIndexFileInsideFolder(entry);

            if (indexFileInsideSomeFolder) {
                throw new Error(messagesAlerts.indexFileInsideFolder);
            } else {
                const indexFileFound = Object.values(entry.files).some((file) => {
                    return file.name === fileToBePresent;
                });

                if (!indexFileFound) {
                    throw new Error(messagesAlerts.hasNotIndexFile);
                }
            }
        });
    };

    const acceptedFileTypes = {
        "application/zip": [".zip"],
        "application/x-zip-compressed": [".zip"],
    };

    return (
        <div className={classes.stepThreeContainer}>
            <Typography variant="subtitle1" className={classes.stepsHeading}>
                Step 3: Upload the Demo Files
            </Typography>
            <Typography variant="subtitle2">
                Zip / Rar / compress all the project files and drop it here.{" "}
            </Typography>
            <SNETFileUpload
                onDrop={handleFileDrop}
                accept={acceptedFileTypes}
                multiple={false}
                showFileDetails
                fileName={selectedFile.name}
                fileSize={selectedFile.size}
                fileDownloadURL={demoFiles.url}
                uploadSuccess={Boolean(demoFiles.url)}
                error={alert?.type === alertTypes.ERROR ? alert : false}
                helperText={
                    <>
                        <Typography>
                            * Compress only the individual files with no parent folders
                        </Typography>
                        <Typography>* Package must be under 2mb</Typography>
                        <Typography>* Make sure the extension is .zip</Typography>
                    </>
                }
            />
            {alert && <AlertBox type={alert.type} message={alert.message} />}
        </div>
    );
};

export default UploadDemoFiles;
